/**
 * Native authentication utilities for Capacitor mobile apps.
 * Handles Google Sign-In and session management for native platforms.
 */

import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SocialLogin } from '@capgo/capacitor-social-login';

// For native apps, always use production backend since localhost won't work
const getApiUrl = () => {
  if (Capacitor.isNativePlatform()) {
    return 'https://api.shkalot.xyz';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5001';
};
const API_URL = getApiUrl();
const SESSION_KEY = 'session_token';

/**
 * Check if running on a native platform (iOS or Android)
 */
export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * Get the current platform
 */
export function getPlatform() {
  return Capacitor.getPlatform(); // 'ios', 'android', or 'web'
}

/**
 * Initialize Google Auth for native platforms.
 * Call this once when the app starts.
 */
export async function initGoogleAuth() {
  if (!isNativePlatform()) {
    return;
  }

  try {
    // Use the web client ID for token verification
    // This is the same client ID used for web OAuth
    const webClientId = '142726731755-fg4c57lqlamdij2oiga6m08eq0cj16oc.apps.googleusercontent.com';
    
    await SocialLogin.initialize({
      google: {
        webClientId: webClientId,
        iOSClientId: '142726731755-2o2n6r209fc7kh5aapbdkno5h7jrie17.apps.googleusercontent.com',
        iOSServerClientId: webClientId,
      },
    });
    console.log('[NativeAuth] Google Auth initialized successfully');
  } catch (error) {
    console.error('[NativeAuth] Failed to initialize Google Auth:', error);
    // Don't throw - allow app to continue, login will handle re-init if needed
  }
}

/**
 * Sign in with Google.
 * On native: Uses native Google Sign-In SDK
 * On web: Redirects to OAuth flow
 */
export async function signInWithGoogle() {
  if (!isNativePlatform()) {
    // Web: Use existing OAuth redirect flow
    window.location.href = `${API_URL}/api/auth/google`;
    return null;
  }

  try {
    // Native: Use Google Sign-In SDK
    console.log('[NativeAuth] Starting Google Sign-In...');
    
    const result = await SocialLogin.login({
      provider: 'google',
      options: {
        scopes: ['profile', 'email'],
      },
    });

    console.log('[NativeAuth] Google Sign-In result:', JSON.stringify(result));

    if (!result || !result.result) {
      throw new Error('Google Sign-In failed: No result');
    }

    // The plugin returns different structures - handle both
    const googleResult = result.result;
    
    // Try to get idToken, fall back to accessToken for verification
    // The accessToken.token is the actual token string
    let idToken = googleResult.idToken;
    let accessToken = googleResult.accessToken?.token || googleResult.accessToken;
    
    console.log('[NativeAuth] idToken present:', !!idToken);
    console.log('[NativeAuth] accessToken present:', !!accessToken);
    
    // If no idToken, we need to use accessToken with a different backend flow
    // For now, require idToken
    if (!idToken && !accessToken) {
      throw new Error('Google Sign-In failed: No token received');
    }

    // Send token to backend for verification
    // Use CapacitorHttp for native platforms to bypass WebView CORS restrictions
    console.log('[NativeAuth] Sending token to backend:', API_URL);
    let data;
    try {
      if (Capacitor.isNativePlatform()) {
        // Use native HTTP for iOS/Android
        const response = await CapacitorHttp.post({
          url: `${API_URL}/api/auth/google/mobile`,
          headers: {
            'Content-Type': 'application/json',
          },
          data: { 
            idToken: idToken || null,
            accessToken: accessToken || null,
            platform: getPlatform(),
          },
        });
        console.log('[NativeAuth] Response status:', response.status);
        if (response.status !== 200) {
          console.error('[NativeAuth] Backend auth error:', response.status, response.data);
          throw new Error(`Authentication failed: ${response.status}`);
        }
        data = response.data;
      } else {
        // Use fetch for web
        const response = await fetch(`${API_URL}/api/auth/google/mobile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            idToken: idToken || null,
            accessToken: accessToken || null,
            platform: getPlatform(),
          }),
        });
        console.log('[NativeAuth] Response status:', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[NativeAuth] Backend auth error:', response.status, errorText);
          throw new Error(`Authentication failed: ${response.status}`);
        }
        data = await response.json();
      }
    } catch (fetchError) {
      console.error('[NativeAuth] Fetch error:', fetchError.message, fetchError.name);
      throw new Error(`Network error: ${fetchError.message}`);
    }
    console.log('[NativeAuth] Backend auth success, user:', data.user?.email);
    
    // Store session token for native apps
    if (data.session_token) {
      await storeSession(data.session_token);
    }

    return {
      success: true,
      user: data.user,
      sessionToken: data.session_token,
    };
  } catch (error) {
    console.error('[NativeAuth] Google Sign-In error:', error);
    throw error;
  }
}

/**
 * Sign out from Google and clear session.
 */
export async function signOut() {
  try {
    if (isNativePlatform()) {
      await SocialLogin.logout({ provider: 'google' });
    }
    await clearSession();
    
    // Also call backend logout
    const token = await getSession();
    await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
  } catch (error) {
    console.error('[NativeAuth] Sign out error:', error);
    // Clear session anyway
    await clearSession();
  }
}

/**
 * Store session token in secure storage (native) or let cookies handle it (web).
 */
export async function storeSession(sessionToken) {
  if (isNativePlatform()) {
    await Preferences.set({ key: SESSION_KEY, value: sessionToken });
    console.log('[NativeAuth] Session stored');
  }
  // Web uses HTTP-only cookies, nothing to store
}

/**
 * Get stored session token.
 */
export async function getSession() {
  if (isNativePlatform()) {
    const { value } = await Preferences.get({ key: SESSION_KEY });
    return value;
  }
  return null; // Web uses cookies
}

/**
 * Clear stored session token.
 */
export async function clearSession() {
  if (isNativePlatform()) {
    await Preferences.remove({ key: SESSION_KEY });
    console.log('[NativeAuth] Session cleared');
  }
}

/**
 * Check if user has a valid session.
 */
export async function hasValidSession() {
  if (isNativePlatform()) {
    const token = await getSession();
    if (!token) return false;
    
    // Verify with backend
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.ok;
    } catch {
      return false;
    }
  }
  
  // Web: Let the existing auth context handle it
  return null;
}

/**
 * Make an authenticated API request.
 * Automatically adds Authorization header for native apps.
 */
export async function authenticatedFetch(url, options = {}) {
  const token = await getSession();
  
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Still include cookies for web
  });
}
